import { RRule, RRuleSet } from 'rrule';
import { Schedule, PendingTransaction, Transaction, UUID } from '../types';

export class SchedulingEngine {
  private static instance: SchedulingEngine;
  
  private constructor() {}
  
  static getInstance(): SchedulingEngine {
    if (!SchedulingEngine.instance) {
      SchedulingEngine.instance = new SchedulingEngine();
    }
    return SchedulingEngine.instance;
  }

  // Parse RRULE string and create RRule instance
  private parseRRule(spec: string, startDate: string): RRule {
    const dtstart = new Date(startDate);
    return RRule.fromString(`DTSTART:${dtstart.toISOString().replace(/[-:]/g, '').split('.')[0]}Z\nRRULE:${spec}`);
  }

  // Compute next due date for a schedule
  computeNextDue(schedule: Schedule, afterDate?: string): string {
    try {
      const after = afterDate ? new Date(afterDate) : new Date();
      const rrule = this.parseRRule(schedule.spec, schedule.startDate);
      
      const nextDate = rrule.after(after, true); // inclusive of after date
      
      if (!nextDate) {
        // No more occurrences (ended or no future dates)
        return '';
      }
      
      // Check if we've passed the end date
      if (schedule.endDate && nextDate > new Date(schedule.endDate)) {
        return '';
      }
      
      return nextDate.toISOString().split('T')[0]; // Return YYYY-MM-DD
    } catch (error) {
      console.error('Error computing next due date:', error);
      return '';
    }
  }

  // Get all due instances between two dates
  getDueInstances(schedule: Schedule, fromDate: string, toDate: string): string[] {
    try {
      const rrule = this.parseRRule(schedule.spec, schedule.startDate);
      const from = new Date(fromDate);
      const to = new Date(toDate);
      
      const instances = rrule.between(from, to, true); // inclusive
      
      return instances
        .filter(date => {
          // Filter out dates past end date
          if (schedule.endDate && date > new Date(schedule.endDate)) {
            return false;
          }
          return true;
        })
        .map(date => date.toISOString().split('T')[0]);
    } catch (error) {
      console.error('Error getting due instances:', error);
      return [];
    }
  }

  // Generate pending transactions for schedules that are due but not posted
  generatePendingTransactions(
    schedules: Schedule[],
    existingTransactions: Transaction[],
    upToDate: string
  ): PendingTransaction[] {
    const pending: PendingTransaction[] = [];
    const today = new Date().toISOString().split('T')[0];
    
    // Create a set of posted schedule instances for quick lookup
    const postedInstances = new Set<string>();
    existingTransactions.forEach(tx => {
      if (tx.scheduleId && tx.scheduleDueDate) {
        postedInstances.add(`${tx.scheduleId}:${tx.scheduleDueDate}`);
      }
    });
    
    schedules.forEach(schedule => {
      if (!schedule.active) return;
      
      // Get all due instances from start date up to the specified date
      const dueInstances = this.getDueInstances(schedule, schedule.startDate, upToDate);
      
      dueInstances.forEach(dueDate => {
        const instanceKey = `${schedule.id}:${dueDate}`;
        
        // Skip if already posted or future date when autoPost is false
        if (postedInstances.has(instanceKey)) return;
        if (!schedule.autoPost && dueDate > today) return;
        
        pending.push({
          id: `pending-${schedule.id}-${dueDate}`,
          scheduleId: schedule.id,
          dueDate,
          template: schedule.template,
          type: schedule.type
        });
      });
    });
    
    return pending.sort((a, b) => a.dueDate.localeCompare(b.dueDate));
  }

  // Create actual transactions from pending items (for auto-post or manual post)
  createTransactionFromPending(
    pending: PendingTransaction, 
    createdBy: UUID,
    overrides?: Partial<Transaction>
  ): Omit<Transaction, 'id' | 'createdAt' | 'updatedAt'> {
    const now = new Date().toISOString();
    
    // Apply sign based on type: income is positive, expense is negative
    const amount = pending.type === 'income' 
      ? Math.abs(pending.template.amount)
      : -Math.abs(pending.template.amount);
    
    return {
      date: pending.dueDate,
      accountId: pending.template.accountId,
      amount,
      payee: pending.template.payee || (pending.type === 'income' ? 'Recurring Income' : 'Recurring Expense'),
      categoryId: pending.template.categoryId,
      memo: pending.template.memo || `Auto-generated from schedule`,
      tags: ['recurring'],
      cleared: true, // Auto-posted transactions are pre-cleared
      reconciled: false,
      createdBy,
      scheduleId: pending.scheduleId,
      scheduleDueDate: pending.dueDate,
      ...overrides
    };
  }

  // Create preset RRULE strings for common frequencies
  static createRRule(frequency: string, options: any = {}): string {
    switch (frequency.toLowerCase()) {
      case 'daily':
        return 'FREQ=DAILY';
      
      case 'weekly':
        const byDay = options.dayOfWeek || 'MO'; // Default to Monday
        return `FREQ=WEEKLY;BYDAY=${byDay}`;
      
      case 'biweekly':
        const biweeklyDay = options.dayOfWeek || 'FR'; // Default to Friday (payday)
        return `FREQ=WEEKLY;INTERVAL=2;BYDAY=${biweeklyDay}`;
      
      case 'monthly':
        if (options.dayOfMonth) {
          return `FREQ=MONTHLY;BYMONTHDAY=${options.dayOfMonth}`;
        } else if (options.weekday) {
          // e.g., "First Friday" = BYDAY=1FR
          return `FREQ=MONTHLY;BYDAY=${options.weekday}`;
        }
        return 'FREQ=MONTHLY;BYMONTHDAY=1'; // Default to 1st of month
      
      case 'quarterly':
        const quarterDay = options.dayOfMonth || 1;
        return `FREQ=MONTHLY;INTERVAL=3;BYMONTHDAY=${quarterDay}`;
      
      case 'semiannually':
        const semiDay = options.dayOfMonth || 1;
        return `FREQ=MONTHLY;INTERVAL=6;BYMONTHDAY=${semiDay}`;
      
      case 'yearly':
        const yearDay = options.dayOfMonth || 1;
        const yearMonth = options.month || 1;
        return `FREQ=YEARLY;BYMONTH=${yearMonth};BYMONTHDAY=${yearDay}`;
      
      default:
        throw new Error(`Unsupported frequency: ${frequency}`);
    }
  }

  // Helper to validate RRULE strings
  validateRRule(spec: string): boolean {
    try {
      RRule.fromString(`RRULE:${spec}`);
      return true;
    } catch {
      return false;
    }
  }

  // Run the scheduler - process all active schedules
  run(
    schedules: Schedule[], 
    existingTransactions: Transaction[],
    today: string = new Date().toISOString().split('T')[0]
  ): {
    autoPosted: PendingTransaction[];
    pendingForUser: PendingTransaction[];
    updatedSchedules: Schedule[];
  } {
    const autoPosted: PendingTransaction[] = [];
    const pendingForUser: PendingTransaction[] = [];
    const updatedSchedules: Schedule[] = [];
    
    // Generate all pending transactions up to today
    const allPending = this.generatePendingTransactions(schedules, existingTransactions, today);
    
    // Split between auto-post and manual
    allPending.forEach(pending => {
      const schedule = schedules.find(s => s.id === pending.scheduleId);
      if (!schedule) return;
      
      if (schedule.autoPost && pending.dueDate <= today) {
        autoPosted.push(pending);
      } else if (!schedule.autoPost) {
        pendingForUser.push(pending);
      }
    });
    
    // Update nextDue for all schedules
    schedules.forEach(schedule => {
      const nextDue = this.computeNextDue(schedule, today);
      if (schedule.nextDue !== nextDue) {
        updatedSchedules.push({
          ...schedule,
          nextDue
        });
      }
    });
    
    return {
      autoPosted,
      pendingForUser,
      updatedSchedules
    };
  }
}

export const schedulingEngine = SchedulingEngine.getInstance();